import { PackageSearch } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/core";

export interface ShopEmptyProps {
  /** La categoría filtrada, si el vacío viene de un filtro. */
  category: string | null;
  onClearCategory: () => void;
}

/**
 * Catálogo vacío.
 *
 * ── Por qué NO parece un error ───────────────────────────────────────
 * Nada de rojo, ni de icono de alerta: que no haya productos en una
 * categoría es un resultado normal, y pintarlo como una falla hace que
 * alguien reporte un problema que no existe. La diferencia con el aviso
 * de `ShopError` tiene que verse a un metro de la pantalla.
 *
 * ── Vacío por filtro y vacío de verdad no son lo mismo ───────────────
 * Con una categoría puesta, el vacío es culpa del filtro y la salida es
 * quitarlo — de ahí el botón. Sin filtro, el catálogo está vacío y no hay
 * nada que la persona pueda hacer: ofrecerle un botón sería burlarse.
 */
export function ShopEmpty({ category, onClearCategory }: ShopEmptyProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-ds-xl border border-ds-border bg-white/70 px-6 py-16 text-center">
      <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-ds-lg border border-ds-lavender-bg-2 bg-ds-mist/40">
        <PackageSearch className="h-7 w-7 text-ds-lavender" aria-hidden="true" />
      </span>

      <p className="text-[15px] font-semibold text-ds-ink">
        {category
          ? t('No hay productos en "{{categoria}}"', { categoria: category })
          : t("Todavía no hay productos publicados")}
      </p>

      <p className="mx-auto mt-2 max-w-sm text-[13px] leading-relaxed text-ds-ink-3">
        {category
          ? t("Prueba con otra categoría o mira el catálogo completo.")
          : t("Estamos preparando el catálogo. Vuelve pronto.")}
      </p>

      {category && (
        <Button variant="dark" size="sm" onClick={onClearCategory} className="mt-6">
          {t("Ver todos")}
        </Button>
      )}
    </div>
  );
}
